import { useState, useMemo, useRef, useEffect } from 'react';
import { RefreshCw, Search, ArrowUpDown, Filter, X, LayoutGrid, Rows } from 'lucide-react';
import { CampaignStatus, CampaignStatusLabels } from '@monitoring-panel/types';
import type { CampaignSummary, ExecutionStatusSummary } from '@monitoring-panel/types';
import { CampaignCard } from '@/components/CampaignCard';
import { CampaignListSkeleton } from '@/components/Skeleton';
import { useCampaigns, useRefreshCache } from '@/hooks/useCampaigns';
import { useBatchSummaries } from '@/hooks/useExecutionNotes';
import { usePersistentState } from '@/hooks/usePersistentState';
import { useExecutionsScrollRestoration } from '@/hooks/useExecutionsScrollRestoration';
import { cn } from '@/lib/utils';

type SortOption = 'endDate' | 'status' | 'budget' | 'pacing' | 'runRate';
type ViewMode = 'compact' | 'wide';

const VIEW_MODE_STORAGE_KEY = 'executionsViewMode';

// Filters live in localStorage so they survive navigating into an execution's
// detail view and back — otherwise the search/status/owner selection is lost
// and the user has to look the campaign up again.
const SEARCH_STORAGE_KEY = 'executionsSearch';
const SORT_STORAGE_KEY = 'executionsSortBy';
const STATUS_STORAGE_KEY = 'executionsStatuses';
const OWNERS_STORAGE_KEY = 'executionsOwners';

const SORT_OPTIONS: SortOption[] = ['endDate', 'status', 'budget', 'pacing', 'runRate'];

/** Group key for clustering executions of the same campaign. */
function campaignGroupKey(c: CampaignSummary): string {
  return c.auxCampaignId ?? c.campaignName;
}

/**
 * Group executions by campaign WITHOUT changing the sort: groups are ordered by
 * the position of their first (earliest, per the active sort) execution, and
 * executions stay in their sorted order within each group.
 */
function groupByCampaign(
  campaigns: CampaignSummary[]
): Array<{ key: string; title: string; items: CampaignSummary[] }> {
  const groups: Array<{ key: string; title: string; items: CampaignSummary[] }> = [];
  const byKey = new Map<string, { key: string; title: string; items: CampaignSummary[] }>();

  for (const c of campaigns) {
    const key = campaignGroupKey(c);
    let group = byKey.get(key);
    if (!group) {
      // Prefer the parent campaign name for the group header; fall back to the
      // execution name when the execution isn't linked to a campaign.
      group = { key, title: c.auxCampaignName ?? c.campaignName, items: [] };
      byKey.set(key, group);
      groups.push(group); // preserves first-seen order = sorted order
    }
    group.items.push(c);
  }

  return groups;
}

const ALL_STATUSES = [
  CampaignStatus.PendingReview,
  CampaignStatus.PendingSetup,
  CampaignStatus.PendingActivation,
  CampaignStatus.InProgress,
  CampaignStatus.Paused,
  CampaignStatus.Done,
  CampaignStatus.Validated,
  CampaignStatus.Discarded,
];

const DEFAULT_STATUSES = [
  CampaignStatus.InProgress,
];

const isStringArray = (value: unknown): boolean =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

/** A persisted status list is only usable if it's non-empty and all values are known. */
const isStatusArray = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((v) => ALL_STATUSES.includes(v as CampaignStatus));

export function ExecutionsView() {
  const [search, setSearch] = usePersistentState<string>(
    SEARCH_STORAGE_KEY,
    '',
    (v) => typeof v === 'string'
  );
  const [sortBy, setSortBy] = usePersistentState<SortOption>(
    SORT_STORAGE_KEY,
    'endDate',
    (v) => SORT_OPTIONS.includes(v as SortOption)
  );
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => (localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode) || 'compact'
  );
  const [selectedStatuses, setSelectedStatuses] = usePersistentState<CampaignStatus[]>(
    STATUS_STORAGE_KEY,
    DEFAULT_STATUSES,
    isStatusArray
  );
  const [selectedOwners, setSelectedOwners] = usePersistentState<string[]>(
    OWNERS_STORAGE_KEY,
    [],
    isStringArray
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isOwnerFilterOpen, setIsOwnerFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const ownerFilterRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useCampaigns({
    status: selectedStatuses,
  });

  useExecutionsScrollRestoration(!isLoading && !error);

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const refreshCache = useRefreshCache();

  const allCampaigns = data?.campaigns || [];

  const allOwners = useMemo(
    () => [...new Set(allCampaigns.map((c) => c.specialist).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [allCampaigns]
  );

  // Close filter dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    }
    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ownerFilterRef.current && !ownerFilterRef.current.contains(event.target as Node)) {
        setIsOwnerFilterOpen(false);
      }
    }
    if (isOwnerFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOwnerFilterOpen]);

  // Batch summary loading — single API call for all unique campaign names
  const campaignNames = useMemo(
    () => [...new Set(allCampaigns.map(c => c.notesGroupKey))],
    [allCampaigns]
  );
  const { data: batchSummaries } = useBatchSummaries(campaignNames);

  const summaryMap = useMemo(() => {
    const map = new Map<string, ExecutionStatusSummary>();
    batchSummaries?.summaries?.forEach(s => map.set(s.campaignName, s));
    return map;
  }, [batchSummaries]);

  // Identify campaigns that share a notesGroupKey (multiple executions)
  const sharedCampaigns = useMemo(() => {
    const nameCount = new Map<string, number>();
    allCampaigns.forEach(c => nameCount.set(c.notesGroupKey, (nameCount.get(c.notesGroupKey) || 0) + 1));
    return new Set([...nameCount.entries()].filter(([, count]) => count > 1).map(([name]) => name));
  }, [allCampaigns]);

  const toggleStatus = (status: CampaignStatus) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        // Don't allow deselecting all statuses
        if (prev.length === 1) return prev;
        return prev.filter((s) => s !== status);
      }
      return [...prev, status];
    });
  };

  const toggleOwner = (owner: string) => {
    setSelectedOwners((prev) =>
      prev.includes(owner) ? prev.filter((o) => o !== owner) : [...prev, owner]
    );
  };

  const handleRefreshAll = () => {
    refreshCache.mutate(undefined);
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="adv-page-title">Executions</h1>
            <p className="adv-section-desc">Loading executions...</p>
          </div>
        </div>
        <CampaignListSkeleton count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="adv-alert">
        Error loading executions: {error.message}
      </div>
    );
  }

  // Client-side filtering (search + owner)
  const ownerFiltered = selectedOwners.length > 0
    ? allCampaigns.filter((campaign) => selectedOwners.includes(campaign.specialist))
    : allCampaigns;

  const filteredCampaigns = search
    ? ownerFiltered.filter((campaign) => {
        const searchLower = search.toLowerCase();
        return (
          campaign.campaignName.toLowerCase().includes(searchLower) ||
          campaign.executionId.toLowerCase().includes(searchLower) ||
          campaign.specialist.toLowerCase().includes(searchLower)
        );
      })
    : ownerFiltered;

  // Sorting
  const sortCampaigns = (campaigns: CampaignSummary[], sort: SortOption): CampaignSummary[] => {
    const sorted = [...campaigns];

    switch (sort) {
      case 'status':
        return sorted.sort((a, b) => a.status - b.status);

      case 'endDate':
        return sorted.sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime());

      case 'budget':
        return sorted.sort((a, b) => {
          const budgetA = a.executionType === 'Deal' ? a.advertiserBudget : a.budget;
          const budgetB = b.executionType === 'Deal' ? b.advertiserBudget : b.budget;
          return budgetB - budgetA;
        });

      case 'pacing':
        return sorted.sort((a, b) => {
          const pacingA = a.executionType === 'Deal' ? a.accumulatedBudgetPacing : a.accumulatedPacing;
          const pacingB = b.executionType === 'Deal' ? b.accumulatedBudgetPacing : b.accumulatedPacing;
          const distanceA = Math.abs(100 - pacingA);
          const distanceB = Math.abs(100 - pacingB);
          return distanceB - distanceA;
        });

      case 'runRate':
        return sorted.sort((a, b) => {
          const rrA = a.budget > 0 ? (a.totalCost / a.budget) * 100 : 0;
          const rrB = b.budget > 0 ? (b.totalCost / b.budget) * 100 : 0;
          return rrB - rrA;
        });

      default:
        return sorted;
    }
  };

  const campaigns = sortCampaigns(filteredCampaigns, sortBy);
  const campaignGroups = groupByCampaign(campaigns);

  // Render blocks (preserves order): a campaign with 2+ executions is its own
  // full-width block (header spans the row, executions in a 2-col grid below);
  // single-execution campaigns flow together in a shared grid so they fill the
  // gaps left→right instead of wasting half a row each.
  type RenderBlock =
    | { kind: 'multi'; group: (typeof campaignGroups)[number] }
    | { kind: 'flow'; groups: typeof campaignGroups };
  const renderBlocks: RenderBlock[] = [];
  for (const group of campaignGroups) {
    if (group.items.length >= 2) {
      renderBlocks.push({ kind: 'multi', group });
    } else {
      const last = renderBlocks[renderBlocks.length - 1];
      if (last && last.kind === 'flow') {
        last.groups.push(group);
      } else {
        renderBlocks.push({ kind: 'flow', groups: [group] });
      }
    }
  }

  const isDefaultFilter = selectedStatuses.length === DEFAULT_STATUSES.length &&
    DEFAULT_STATUSES.every((s) => selectedStatuses.includes(s));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="adv-page-title">Executions</h1>
          <p className="adv-section-desc">
            {search || selectedOwners.length > 0
              ? `${campaigns.length} of ${allCampaigns.length} executions`
              : `${campaigns.length} executions`}
          </p>
        </div>

        <button
          onClick={handleRefreshAll}
          disabled={refreshCache.isPending}
          className={cn(
            'adv-btn adv-btn--primary w-full sm:w-auto'
          )}
        >
          <RefreshCw size={16} className={cn(refreshCache.isPending && 'animate-spin')} />
          Refresh All
        </button>
      </div>

      {/* Search, Filter, and Sort */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-adv-tertiary pointer-events-none" size={20} />
          <input
            type="text"
            placeholder="Search executions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="adv-input w-full pl-10 pr-10"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              title="Clear search"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-adv-tertiary hover:text-adv-primary"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={cn(
              'adv-btn adv-btn--secondary w-full sm:w-auto h-11',
              !isDefaultFilter && 'border-adv-gold bg-adv-gold-veil'
            )}
          >
            <Filter size={16} />
            <span>Status</span>
            {!isDefaultFilter && (
              <span className="adv-num bg-adv-gold text-adv-on-gold text-adv-code-xs rounded-full w-5 h-5 flex items-center justify-center">
                {selectedStatuses.length}
              </span>
            )}
          </button>

          {isFilterOpen && (
            <div className="adv-dropdown absolute right-0 mt-2 w-56 z-20">
              <div className="px-3 py-2 flex items-center justify-between border-b border-adv-border-subtle mb-1">
                <span className="adv-kpi__label">Filter by Status</span>
                {!isDefaultFilter && (
                  <button
                    onClick={() => setSelectedStatuses(DEFAULT_STATUSES)}
                    className="adv-link text-adv-body-s"
                  >
                    Reset
                  </button>
                )}
              </div>
              {ALL_STATUSES.map((status) => (
                <label
                  key={status}
                  className="adv-dropdown__item"
                >
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(status)}
                    onChange={() => toggleStatus(status)}
                    className="adv-check"
                  />
                  <span>{CampaignStatusLabels[status]}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Owner Filter */}
        <div className="relative" ref={ownerFilterRef}>
          <button
            onClick={() => setIsOwnerFilterOpen(!isOwnerFilterOpen)}
            disabled={allOwners.length === 0}
            className={cn(
              'adv-btn adv-btn--secondary w-full sm:w-auto h-11',
              selectedOwners.length > 0 && 'border-adv-gold bg-adv-gold-veil'
            )}
          >
            <Filter size={16} />
            <span>Owner</span>
            {selectedOwners.length > 0 && (
              <span className="adv-num bg-adv-gold text-adv-on-gold text-adv-code-xs rounded-full w-5 h-5 flex items-center justify-center">
                {selectedOwners.length}
              </span>
            )}
          </button>

          {isOwnerFilterOpen && (
            <div className="adv-dropdown absolute right-0 mt-2 w-64 z-20 max-h-80 overflow-y-auto">
              <div className="px-3 py-2 flex items-center justify-between border-b border-adv-border-subtle mb-1">
                <span className="adv-kpi__label">Filter by Owner</span>
                {selectedOwners.length > 0 && (
                  <button
                    onClick={() => setSelectedOwners([])}
                    className="adv-link text-adv-body-s"
                  >
                    Reset
                  </button>
                )}
              </div>
              {allOwners.length === 0 ? (
                <div className="px-3 py-2 text-adv-body-s text-adv-secondary">No owners available</div>
              ) : (
                allOwners.map((owner) => (
                  <label
                    key={owner}
                    className="adv-dropdown__item"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOwners.includes(owner)}
                      onChange={() => toggleOwner(owner)}
                      className="adv-check"
                    />
                    <span className="truncate">{owner}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Sort */}
          <div className="relative flex-1 sm:w-auto sm:min-w-[200px]">
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 text-adv-tertiary pointer-events-none" size={16} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="adv-input w-full pl-9 pr-4 appearance-none cursor-pointer"
            >
              <option value="status">Sort by Status</option>
              <option value="endDate">Sort by End Date</option>
              <option value="budget">Sort by Budget</option>
              <option value="pacing">Sort by Pacing Gap</option>
              <option value="runRate">Sort by Cost RR</option>
            </select>
          </div>

          {/* View mode toggle: compact (2-up cards) vs wide (1-up with chart preview) */}
          <div className="flex rounded-adv-pill border border-adv-border-default bg-adv-card p-1" role="group" aria-label="Card view mode">
            <button
              type="button"
              onClick={() => setViewMode('compact')}
              aria-pressed={viewMode === 'compact'}
              title="Compact view"
              className={cn(
                'flex items-center justify-center p-1.5 rounded-adv-pill transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus',
                viewMode === 'compact' ? 'bg-adv-gold text-adv-on-gold' : 'text-adv-secondary hover:text-adv-primary'
              )}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('wide')}
              aria-pressed={viewMode === 'wide'}
              title="Wide view with chart preview"
              className={cn(
                'flex items-center justify-center p-1.5 rounded-adv-pill transition-colors focus-visible:outline-none focus-visible:shadow-adv-focus',
                viewMode === 'wide' ? 'bg-adv-gold text-adv-on-gold' : 'text-adv-secondary hover:text-adv-primary'
              )}
            >
              <Rows size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {(!isDefaultFilter || selectedOwners.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {!isDefaultFilter && selectedStatuses.map((status) => (
            <span
              key={`status-${status}`}
              className="adv-tag adv-tag--gold"
            >
              {CampaignStatusLabels[status]}
              <button
                onClick={() => toggleStatus(status)}
                className="opacity-60 hover:opacity-100 disabled:opacity-30"
                disabled={selectedStatuses.length === 1}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          {selectedOwners.map((owner) => (
            <span
              key={`owner-${owner}`}
              className="adv-tag adv-tag--gold"
            >
              Owner: {owner}
              <button
                onClick={() => toggleOwner(owner)}
                className="opacity-60 hover:opacity-100 disabled:opacity-30"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Campaign Grid — multi-execution campaigns are full-width blocks with a
          spanning header; single-execution campaigns flow together to fill gaps. */}
      {campaigns.length === 0 ? (
        <div className="text-center py-12 text-adv-body-m text-adv-secondary">
          No executions found
        </div>
      ) : (
        <div className="space-y-8">
          {renderBlocks.map((block) =>
            block.kind === 'multi' ? (
              <section key={`multi-${block.group.key}`}>
                {renderCampaignHeader(block.group.title, block.group.items.length)}
                <div
                  className={cn(
                    'grid grid-cols-1 gap-4 sm:gap-6 items-start',
                    viewMode === 'compact' && 'md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2'
                  )}
                >
                  {block.group.items.map((campaign) => renderCard(campaign))}
                </div>
              </section>
            ) : (
              <div
                key={`flow-${block.groups[0].key}`}
                className={cn(
                  'grid grid-cols-1 gap-4 sm:gap-6 items-start',
                  viewMode === 'compact' && 'md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2'
                )}
              >
                {block.groups.map((group) => (
                  <div key={group.key} className="flex flex-col">
                    {renderCampaignHeader(group.title, group.items.length)}
                    {renderCard(group.items[0])}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );

  function renderCampaignHeader(title: string, count: number) {
    return (
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-adv-body-m font-bold text-adv-primary truncate">{title}</h2>
        <span className="text-adv-body-s text-adv-tertiary flex-shrink-0">
          <span className="adv-num">{count}</span> execution{count === 1 ? '' : 's'}
        </span>
        <div className="flex-1 border-t border-adv-border" />
      </div>
    );
  }

  function renderCard(campaign: CampaignSummary) {
    return (
      <CampaignCard
        key={campaign.executionId}
        campaign={campaign}
        executionSummary={summaryMap.get(campaign.notesGroupKey)}
        isSharedGroup={sharedCampaigns.has(campaign.notesGroupKey)}
        viewMode={viewMode}
        onRefresh={() => refreshCache.mutate(campaign.executionId)}
        isRefreshing={refreshCache.isPending}
      />
    );
  }
}
